// Page-side attachment helpers: upload with progress, thumbnails, Service Worker
// streaming registration, and whole-file decrypt for images and downloads.
import type { MediaRef, Uuid } from '../api/types'
import { decryptAll } from './attachments'
import { apiUrl, appInvoke, isApp, serverBase } from '../platform'

export type MediaKind = 'image' | 'audio' | 'video' | 'file'

export function mediaKind(mime: string): MediaKind {
  if (/^image\/(png|jpe?g|gif|webp|avif)$/.test(mime)) return 'image'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/')) return 'video'
  return 'file'
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

/** Uploads ciphertext with progress (fetch can't report upload progress). */
export function uploadCiphertext(
  token: string,
  conv: Uuid,
  body: Blob,
  onProgress?: (fraction: number) => void,
): Promise<{ id: Uuid; size: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', apiUrl(`/conversations/${conv}/media`))
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText))
      else {
        let msg = `upload failed (${xhr.status})`
        try {
          msg = JSON.parse(xhr.responseText).message ?? msg
        } catch {
          // keep default
        }
        reject(new Error(msg))
      }
    }
    xhr.onerror = () => reject(new Error('upload failed: network error'))
    xhr.send(body)
  })
}

/** A small JPEG preview (inline in the encrypted message) plus dimensions. */
export async function imageThumbnail(
  file: Blob,
  max = 320,
): Promise<{ thumb: string; width: number; height: number } | null> {
  try {
    const bmp = await createImageBitmap(file)
    const scale = Math.min(1, max / Math.max(bmp.width, bmp.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bmp.width * scale))
    canvas.height = Math.max(1, Math.round(bmp.height * scale))
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    const thumb = canvas.toDataURL('image/jpeg', 0.7)
    const out = { thumb, width: bmp.width, height: bmp.height }
    bmp.close()
    return out
  } catch {
    return null
  }
}

/** Query string routing a media fetch to the server that stores it (federation). */
export function hostQuery(host: string | undefined | null): string {
  return host ? `?host=${encodeURIComponent(host)}` : ''
}

/** Downloads and decrypts a whole attachment. */
export async function fetchAndDecrypt(ref: MediaRef, token: string, host?: string | null): Promise<Blob> {
  const resp = await fetch(apiUrl(`/media/${ref.id}${hostQuery(ref.server ?? host)}`), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) throw new Error(`download failed (${resp.status})`)
  const plain = await decryptAll(ref, await resp.arrayBuffer())
  return new Blob([plain], { type: ref.mime })
}

/**
 * Downloads an attachment in full, keeps it on this device and tells the server, which
 * deletes its copy once every device it was for has it (HRTH-110). Already kept: no
 * download. Throws `gone` when the server no longer has it.
 */
export async function keepAttachment(ref: MediaRef, token: string, host?: string | null): Promise<Blob> {
  const { keepLocally, localAttachment } = await import('./attachmentStore')
  const have = await localAttachment(ref.id)
  if (have) return have
  let blob: Blob
  try {
    blob = await fetchAndDecrypt(ref, token, host)
  } catch (e) {
    throw (e as Error).message.includes('(404)') ? new AttachmentGone() : e
  }
  await keepLocally(ref.id, blob)
  await fetch(apiUrl(`/media/${ref.id}/delivered${hostQuery(ref.server ?? host)}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => undefined)
  return blob
}

/** The server deleted it (delivered to everyone, or 30 days old) and this device hasn't it. */
export class AttachmentGone extends Error {
  constructor() {
    super('No longer on the server: attachments are kept there 30 days at most')
  }
}

let swReady: Promise<ServiceWorker | null> | null = null

/**
 * Registers the streaming Service Worker (no-op where unsupported) and returns the one in
 * charge now: a newer worker can take over while the page is open (HRTH-125), and media
 * must be registered with the worker that will answer for it.
 */
export async function startMediaWorker(): Promise<ServiceWorker | null> {
  const first = await registerMediaWorker()
  return first && (navigator.serviceWorker.controller ?? first)
}

function registerMediaWorker(): Promise<ServiceWorker | null> {
  swReady ??= (async () => {
    // The apps load from their own origin, which the worker can't fetch the server from (and
    // webviews have no Web Push): their shell streams media (registerStream) and pushes.
    if (isApp || !('serviceWorker' in navigator)) return null
    navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
      const d = e.data as { type?: string; id?: string }
      const msg = d?.type === 'hrth-need' && d.id ? streams.get(d.id) : undefined
      if (msg && e.source) (e.source as ServiceWorker).postMessage(msg)
    })
    navigator.serviceWorker.startMessages()
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      // A worker keeps the CSP its script came with, and registering again doesn't look for
      // a new one: check now, so storage added since the worker started is reachable (HRTH-125).
      await reg.update().catch(() => undefined)
      await navigator.serviceWorker.ready
      // The page must be controlled (by the newest worker) for /media-stream/ requests to reach it.
      if (!navigator.serviceWorker.controller || reg.installing || reg.waiting) {
        await new Promise<void>((res) => {
          navigator.serviceWorker.addEventListener('controllerchange', () => res(), { once: true })
          setTimeout(res, 3000)
        })
      }
      return navigator.serviceWorker.controller ?? reg.active
    } catch {
      return null
    }
  })()
  return swReady
}

/** Gives the worker what it needs to stream `ref`. Returns the stream URL, or null. */
export function streamUrl(ref: MediaRef, token: string, host?: string | null): Promise<string | null> {
  return registerStream({
    type: 'hrth-media',
    id: ref.id,
    info: { key: ref.key, nonce: ref.nonce, size: ref.size, chunk: ref.chunk },
    mime: ref.mime,
    token,
    host: ref.server ?? host ?? null,
  })
}

/** What the worker was told about each stream, to tell a newer worker again. */
const streams = new Map<string, { type: string; id: string }>()
/** Makes each stream URL unique (the worker reads only the path): Firefox shares one
 * download between elements with the same URL, so a new player of a file inherits an
 * old one's dead or cancelled load and never starts. */
let played = 0

/**
 * Registers a stream with the worker in charge; returns its URL, or null where there's no
 * worker. A worker that took over since (HRTH-125) asks for streams it doesn't know.
 */
export async function registerStream(msg: { type: 'hrth-media' | 'hrth-library'; id: string; [k: string]: unknown }): Promise<string | null> {
  // The apps stream through their shell instead (apps/tauri/src/media.rs): same message.
  if (isApp) return appInvoke<string>('media_register', { server: serverBase(), stream: msg }).catch(() => null)
  const sw = await startMediaWorker()
  if (!sw) return null
  streams.set(msg.id, msg)
  const ok = await new Promise<boolean>((resolve) => {
    const ch = new MessageChannel()
    ch.port1.onmessage = () => resolve(true)
    setTimeout(() => resolve(false), 2000)
    sw.postMessage(msg, [ch.port2])
  })
  return ok ? `/media-stream/${msg.id}?p=${++played}` : null
}
